/**
 * BidService — HttpService wrapper + Bid<->API mapping for the /bids backend (P1/P2).
 *
 * bidStore.ts calls these; components never see the API shape. All the awkward mapping lives here:
 *   - the frontend Bid.id is the bid-number string; the API addresses bids by their UUID
 *   - camelCase (frontend) <-> snake_case (API)
 *   - row_length_m / profit_pct live on the BID header in the API, but on the WORKBOOK in the UI
 */
import HttpService, { type HttpError } from '../AppSettings/services/HttpService';
import type { Bid } from './bidStore';
import type { BidWorkbook, BidPhase, BidLineItem } from './BidDetail/bidWorkbook';

const http = new HttpService();

/** A stored bid carries the server UUID + margins alongside the display Bid. */
export interface StoredBid {
  bid: Bid;
  uuid: string;
  rowLengthM: number;
  /** undefined when the server omitted it (financially-blind caller). */
  profitPct?: number;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function apiToStored(api: any): StoredBid {
  return {
    uuid: api.id,
    rowLengthM: num(api.row_length_m),
    profitPct: api.profit_pct === undefined || api.profit_pct === null
      ? undefined
      : num(api.profit_pct),
    bid: {
      id: api.bid_number,
      name: api.project_title ?? '',
      client: api.client ?? '',
      // '' rather than undefined so the <select> is a controlled component from the first render.
      clientContactId: api.client_contact_id ?? '',
      // The project this bid became, if any. Drives Convert-vs-Open on the detail page without a
      // second request — it rides along on every bid read (BID_KEYS).
      convertedMatterid: api.converted_matterid ?? undefined,
      area: api.area ?? '',
      estimator: api.estimator ?? '',
      status: api.status as Bid['status'],
      // undefined (not 0) when the server OMITTED it — a blind role gets no money at all, and
      // rendering "$0" would claim the bid is worth nothing. financialDisplay.money() shows "—".
      bidValue: api.computed_bid_total === undefined || api.computed_bid_total === null
        ? undefined
        : num(api.computed_bid_total),
      // target_margin and profit_pct are stripped server-side exactly like dollars, so an absent
      // one must stay undefined — a fabricated "0% margin" beside a redacted total is the same
      // falsehood the bidValue treatment prevents.
      targetMargin: api.target_margin === undefined || api.target_margin === null
        ? undefined
        : num(api.target_margin),
      scope: api.scope ?? '',
      createdDate: api.created_date ?? '',
      dueDate: api.due_date ?? '',
      description: api.description ?? '',
      // COVER outcome block (3.7). awarded_value is money: absent (unrecorded OR stripped)
      // must stay undefined — the bidValue treatment. The derived trio is server-computed
      // from the authoritative rollups; it is read here and NEVER sent back.
      awardedTo: api.awarded_to ?? '',
      awardedValue: api.awarded_value === undefined || api.awarded_value === null
        ? undefined : num(api.awarded_value),
      bidderCount: api.bidder_count === undefined || api.bidder_count === null
        ? undefined : num(api.bidder_count),
      awardDiff: api.award_diff === undefined || api.award_diff === null
        ? undefined : num(api.award_diff),
      awardedPerM: api.awarded_per_m === undefined || api.awarded_per_m === null
        ? undefined : num(api.awarded_per_m),
      awardDiffPerM: api.award_diff_per_m === undefined || api.award_diff_per_m === null
        ? undefined : num(api.award_diff_per_m),
      // COVER administrative block (3.8). bonds_required is tri-state — null stays undefined
      // ("unrecorded"), never coerced to false.
      bidMeetingDate: api.bid_meeting_date ?? '',
      companyRep: api.company_rep ?? '',
      otherBidders: api.other_bidders ?? '',
      bondsRequired: api.bonds_required === undefined || api.bonds_required === null
        ? undefined : Boolean(api.bonds_required),
      startDate: api.start_date ?? '',
      completionDate: api.completion_date ?? '',
      // The recorded client discount (4.4) — money: absent stays undefined, never 0%.
      discountPct: api.discount_pct === undefined || api.discount_pct === null
        ? undefined : num(api.discount_pct),
    },
  };
}

/** frontend Bid fields -> API bid-header payload (only defined fields; row_length/profit go with the workbook). */
export function bidToPayload(patch: Partial<Bid>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.name !== undefined) out.project_title = patch.name;
  if (patch.client !== undefined) out.client = patch.client;
  // '' -> null: un-picking the contact must actually clear it. The server treats an explicit null
  // as a clear (CASE WHEN, not COALESCE) and normalises '' to null anyway, but sending null here
  // keeps the intent visible in the request rather than relying on the far end to interpret ''.
  if (patch.clientContactId !== undefined) out.client_contact_id = patch.clientContactId || null;
  if (patch.area !== undefined) out.area = patch.area;
  if (patch.estimator !== undefined) out.estimator = patch.estimator;
  if (patch.status !== undefined) out.status = patch.status;
  if (patch.targetMargin !== undefined) out.target_margin = patch.targetMargin;
  if (patch.scope !== undefined) out.scope = patch.scope;
  if (patch.description !== undefined) out.description = patch.description;
  // Dates: '' (an unset date input) becomes null so the DATE column accepts it.
  if (patch.createdDate !== undefined) out.created_date = patch.createdDate || null;
  if (patch.dueDate !== undefined) out.due_date = patch.dueDate || null;
  // COVER outcome block (3.7). The DERIVED figures (awardDiff / per-metre trio) are deliberately
  // unmapped: the server computes them at read and a payload key for them would be a way to
  // write analytics that disagree with their own inputs.
  if (patch.awardedTo !== undefined) out.awarded_to = patch.awardedTo || null;
  // null = an explicit clear (the emptied number input); the server's CASE-WHEN update honours it.
  if (patch.awardedValue !== undefined) out.awarded_value = patch.awardedValue;
  if (patch.bidderCount !== undefined) out.bidder_count = patch.bidderCount;
  // COVER administrative block (3.8); bonds_required: null clears back to "unrecorded".
  if (patch.bidMeetingDate !== undefined) out.bid_meeting_date = patch.bidMeetingDate || null;
  if (patch.companyRep !== undefined) out.company_rep = patch.companyRep || null;
  if (patch.otherBidders !== undefined) out.other_bidders = patch.otherBidders || null;
  if (patch.bondsRequired !== undefined) out.bonds_required = patch.bondsRequired;
  if (patch.startDate !== undefined) out.start_date = patch.startDate || null;
  if (patch.completionDate !== undefined) out.completion_date = patch.completionDate || null;
  // 4.4: recorded, never applied; null = an explicit clear (the CASE-WHEN path).
  if (patch.discountPct !== undefined) out.discount_pct = patch.discountPct;
  return out;
}

export function apiToWorkbook(apiPhases: any[], rowLengthM: number, profitPct?: number): BidWorkbook {
  const phases: BidPhase[] = (apiPhases ?? []).map((p) => ({
    id: p.id,
    name: p.name ?? '',
    distanceM: num(p.distance_m),
    productionPerDayM: num(p.production_per_day_m),
    specialConditions: p.special_conditions ?? '',
    // Round-tripped, not just read: a mapping that dropped this would wipe the override on the
    // next save — the exact 1.3 bug one layer up.
    profitRateOverride: p.profit_rate_override === null || p.profit_rate_override === undefined
      ? undefined : num(p.profit_rate_override),
    overheadRateOverride: p.overhead_rate_override === null || p.overhead_rate_override === undefined
      ? undefined : num(p.overhead_rate_override),
    phaseTemplateId: p.phase_template_id ?? undefined,
    // Welding calculator inputs (3.10) — same round-trip obligation as the overrides: a mapping
    // that dropped these would wipe them on the next replace-all save.
    jointLengthM: p.joint_length_m === null || p.joint_length_m === undefined
      ? undefined : num(p.joint_length_m),
    weldsPerDay: p.welds_per_day === null || p.welds_per_day === undefined
      ? undefined : num(p.welds_per_day),
    // Sub-quote comparison (3.9). quote_value may be ABSENT two ways — stripped (blind
    // caller) or genuinely unpriced — and both must stay undefined, never 0 (the bidValue
    // rule for vendor money).
    subQuotes: (p.sub_quotes ?? []).map((q: any) => ({
      vendor: q.vendor ?? '',
      quoteValue: q.quote_value === null || q.quote_value === undefined
        ? undefined : num(q.quote_value),
      qualifiers: q.qualifiers ?? '',
      isSuccessful: Boolean(q.is_successful),
    })),
    items: (p.lines ?? []).map(
      (l: any): BidLineItem => ({
        id: l.id,
        description: l.description ?? '',
        kind: l.kind,
        source: l.source,
        qty: num(l.qty),
        hrs: num(l.hrs),
        days: num(l.days),
        rate: num(l.rate),
        // catalog provenance (P3) — undefined rather than null so it round-trips cleanly
        equipmentItemId: l.equipment_item_id ?? undefined,
        labourRoleId: l.labour_role_id ?? undefined,
        costCodeId: l.cost_code_id ?? undefined,
      }),
    ),
  }));
  return { rowLengthM, profitPct, phases };
}

export function workbookToPayload(wb: BidWorkbook): { phases: unknown[] } {
  return {
    phases: wb.phases.map((p, i) => ({
      name: p.name,
      sequence: i,
      distance_m: p.distanceM,
      production_per_day_m: p.productionPerDayM,
      special_conditions: p.specialConditions,
      profit_rate_override: p.profitRateOverride ?? null,
      overhead_rate_override: p.overheadRateOverride ?? null,
      phase_template_id: p.phaseTemplateId ?? null,
      // Sent explicitly (null when unset): the PUT is a replace-all, so omitting either would
      // wipe a stored calculator state on every save. The schema refuses half a state, so the
      // pair is always both-or-neither by the time it reaches the wire.
      joint_length_m: p.jointLengthM ?? null,
      welds_per_day: p.weldsPerDay ?? null,
      // Sub quotes (3.9) — always sent (replace-all), empty list when none. Vendorless rows
      // NEVER reach the wire: the schema refuses them, and one just-added empty row would 400
      // the WHOLE workbook save — poisoning every edit until someone found the row (review;
      // the MobLoadsCard filter precedent). A vendorless row lives locally until named, and
      // the panel says so beside it.
      sub_quotes: (p.subQuotes ?? [])
        .filter((q) => q.vendor.trim())
        .map((q) => ({
          vendor: q.vendor,
          quote_value: q.quoteValue ?? null,
          qualifiers: q.qualifiers || null,
          is_successful: q.isSuccessful,
        })),
      lines: p.items.map((it, j) => ({
        description: it.description,
        kind: it.kind,
        source: it.source,
        qty: it.qty,
        hrs: it.hrs,
        days: it.days,
        rate: it.rate,
        // null (not undefined) so an unset FK is sent explicitly and clears on replace-all
        equipment_item_id: it.equipmentItemId ?? null,
        labour_role_id: it.labourRoleId ?? null,
        cost_code_id: it.costCodeId ?? null,
        sequence: j,
      })),
    })),
  };
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function listBids(): Promise<StoredBid[]> {
  const res = await http.get<any>('/bids');
  return (res?.data?.bids ?? []).map(apiToStored);
}

/** One bid by its server UUID — the read the project header's Bid # chip rides (A5), keyed on
 * `matters.bid_id`. The route is open to the tenant (money server-stripped for blind callers),
 * so the bid_number identity always survives. */
export async function fetchBid(uuid: string): Promise<StoredBid> {
  const res = await http.get<any>(`/bids/${uuid}`);
  return apiToStored(res.data.bid);
}

export async function createBid(
  patch: Partial<Bid> = {},
  margins: { rowLengthM?: number; profitPct?: number } = {},
): Promise<StoredBid> {
  // profit_pct is OMITTED when the caller has none, never defaulted to a number invented here:
  // the server seeds the bid header from the tenant's configured profitRate, and a client-side
  // fallback would silently outrank that (the tenant sets 15% in Config Defaults, every bid is
  // still created at 30%). An explicit value from the create form is passed through unchanged.
  const body: Record<string, unknown> = {
    ...bidToPayload(patch),
    row_length_m: margins.rowLengthM ?? 0,
  };
  if (margins.profitPct !== undefined && margins.profitPct !== null) {
    body.profit_pct = margins.profitPct;
  }
  const res = await http.post<any>('/bids', body);
  return apiToStored(res.data.bid);
}

export async function patchBid(uuid: string, patch: Partial<Bid>): Promise<StoredBid> {
  const res = await http.put<any>(`/bids/${uuid}`, bidToPayload(patch));
  return apiToStored(res.data.bid);
}

/** What a successful conversion returns. `scheduled` is false for a bid with no phases. */
export interface ConversionResult {
  matterid: string;
  scheduled: boolean;
  phases: number;
}

/**
 * The bid already has a project. Carries it, because that is the whole point of the server's 409:
 * two people clicking Convert is normal and the loser must be able to OPEN what the winner made,
 * not be told "already converted" and left to find it.
 */
export class AlreadyConvertedError extends Error {
  readonly matterid: string;

  constructor(message: string, matterid: string) {
    super(message);
    this.name = 'AlreadyConvertedError';
    this.matterid = matterid;
  }
}

export interface ConvertOptions {
  /** Employee to lead the project (matters.employeeid). */
  employeeId?: string;
  /** Schedule anchor; the server defaults to today. */
  anchorDate?: string;
}

export async function convertToProject(
  uuid: string,
  opts: ConvertOptions = {},
): Promise<ConversionResult> {
  const body: Record<string, unknown> = {};
  if (opts.anchorDate) body.anchor_date = opts.anchorDate;
  if (opts.employeeId) body.employee_id = opts.employeeId;
  try {
    const res = await http.post<any>(`/bids/${uuid}/convert-to-project`, body);
    return res.data as ConversionResult;
  } catch (err) {
    const e = err as HttpError;
    if (e.status === 409) {
      // One shape only: axiosInterceptors sets `normalized.data` to the whole response body, and
      // the backend always wraps through GenericResponse — so the matterid sits at data.data.
      // An earlier version also probed a flattened `body.matterid`, which nothing can emit.
      const body = e.data as { data?: { matterid?: string }; error?: string } | undefined;
      const matterid = body?.data?.matterid;
      if (matterid) {
        throw new AlreadyConvertedError(body?.error ?? 'This bid already has a project.', matterid);
      }
    }
    throw err;
  }
}

export async function removeBid(uuid: string): Promise<void> {
  await http.delete(`/bids/${uuid}`);
}

export async function fetchWorkbook(uuid: string): Promise<BidWorkbook> {
  const [bidRes, wbRes] = await Promise.all([
    http.get<any>(`/bids/${uuid}`),
    http.get<any>(`/bids/${uuid}/workbook`),
  ]);
  const bid = bidRes.data.bid;
  return apiToWorkbook(wbRes.data.workbook.phases, num(bid.row_length_m), num(bid.profit_pct));
}

/** Persist the workbook: margins go on the bid header, phases via the workbook PUT. Returns the bid total. */
export async function saveWorkbook(uuid: string, wb: BidWorkbook): Promise<number> {
  await http.put(`/bids/${uuid}`, { row_length_m: wb.rowLengthM, profit_pct: wb.profitPct });
  const res = await http.put<any>(`/bids/${uuid}/workbook`, workbookToPayload(wb));
  return num(res.data.totals.bid_total);
}

// ── Margin params (gap review 1.4) ──────────────────────────────────────────────────────────────

/** The margin/config fields a financial role may override per bid, as the API speaks them.
 * Strings on read (NUMERIC-safe); numbers on write. Only `profit_rate` reaches a BID total today;
 * `overhead_on_revenue_pct` reaches the converted project's daily P&L (Tier 4.1 rider) — the rest
 * persist for the WGL engine design §8 gates on business confirmation. */
export interface MarginParams {
  profit_rate?: string | null;
  overhead_rate?: string | null;
  overhead_mode?: string | null;
  markup_rate?: string | null;
  camp_rate?: string | null;
  load_rate?: string | null;
  fluid_rate?: string | null;
  fluid_days?: string | null;
  fill_factor?: string | null;
  customer_materials?: string | null;
  /** Corporate overhead ON REVENUE (percent-as-number), seeded from the company default at bid
   * creation and consumed by the project P&L. NOT `overhead_rate` above — that is the 2% COST
   * overhead, answered `included` and inert. */
  overhead_on_revenue_pct?: string | null;
}

export interface MarginView {
  margin_params: MarginParams;
  resolved: {
    effective: Record<string, string | null>;
    source: Record<string, string>;
    sheet_overrides: { phase_id: string; name: string; profit_rate: string | null;
                       overhead_rate: string | null }[];
    applied: string[];
  };
}

/** 403 (a blind role) surfaces as null — the panel simply does not render. */
export async function fetchMarginParams(uuid: string): Promise<MarginView | null> {
  try {
    const res = await http.get<any>(`/bids/${uuid}/margin-params`);
    return res.data as MarginView;
  } catch (err: any) {
    if (err?.status === 403) return null;
    throw err;
  }
}

export interface RateCardEquipmentRow {
  id: string;
  name: string;
  category: string | null;
  unit: string;
  default_isr: string | null;
  default_osr: string | null;
}

export interface RateCardLabourRow {
  id: string;
  name: string;
  unit: string;
  default_rate: string | null;
  /** The MSA force-account charge-out — FA labour prices from THIS, not from default_rate. */
  default_fa_rate: string | null;
}

/** One frozen pipe spec — the fluid calculator's m³/m lookup (gap 4.1). `m3PerM` stays null
 * when the frozen row carries no figure: the calculator refuses to derive a volume from it
 * rather than treating absence as 0. */
export interface RateCardFluidSpecRow {
  designation: string;
  m3PerM: number | null;
}

/** The frozen snapshot's catalogs as the detail page reads them: the two rate catalogs the
 * MSA panel uses, plus the pipe specs the fluid calculator prices from (same snapshot
 * discipline — never the live catalog). */
export interface RateCardView {
  equipment: RateCardEquipmentRow[];
  labour: RateCardLabourRow[];
  fluidSpecs: RateCardFluidSpecRow[];
}

/** 403 (a blind role) and 404 (a pre-snapshot bid with no frozen card) both surface as null —
 * the panel simply does not render.
 *
 * The endpoint returns the whole snapshot ROW under `rate_card`; the catalogs live one level
 * down at `rate_card.snapshot` (useRateCardOptions reads the same path). The first version
 * returned the row itself, so `card.equipment` was always undefined and the panel reported an
 * empty card for every bid that HAD one (review, proven live). */
export async function fetchRateCard(uuid: string): Promise<RateCardView | null> {
  try {
    const res = await http.get<any>(`/bids/${uuid}/rate-card`);
    const snapshot = res?.data?.rate_card?.snapshot;
    if (!snapshot) return null;
    return {
      equipment: snapshot.equipment ?? [],
      labour: snapshot.labour ?? [],
      // A snapshot frozen before the pipe-spec table was populated simply has none — the
      // fluid calculator discloses that instead of falling back to the live catalog.
      fluidSpecs: (snapshot.fluid_pipe_specs ?? []).map((r: any): RateCardFluidSpecRow => {
        const m3 = parseFloat(r?.m3_per_m);
        return {
          designation: String(r?.designation ?? ''),
          m3PerM: Number.isFinite(m3) ? m3 : null,
        };
      }),
    };
  } catch (err: any) {
    if (err?.status === 403 || err?.status === 404) return null;
    throw err;
  }
}

export async function saveMarginParams(
  uuid: string, patch: Record<string, number | string | null>,
): Promise<MarginView> {
  const res = await http.put<any>(`/bids/${uuid}/margin-params`, patch);
  return res.data as MarginView;
}

// ── Mob/Demob load matrix (gap 3.11) ────────────────────────────────────────────────────────────

/** One matrix row: an equipment type and how many heavy / legal / pickup loads move it.
 * Counts only — the loadRate pricing stays deferred on the business confirmation (gap 4.1). */
export interface MobLoadRow {
  label: string;
  heavyLoads: number;
  legalLoads: number;
  pickupLoads: number;
}

const mobRowFromApi = (r: any): MobLoadRow => ({
  label: r.label ?? '',
  heavyLoads: num(r.heavy_loads),
  legalLoads: num(r.legal_loads),
  pickupLoads: num(r.pickup_loads),
});

export async function fetchMobLoads(uuid: string): Promise<MobLoadRow[]> {
  const res = await http.get<any>(`/bids/${uuid}/mob-loads`);
  return (res?.data?.rows ?? []).map(mobRowFromApi);
}

/** Replace-all save (the workbook's semantics); returns the persisted rows in order. */
export async function saveMobLoads(uuid: string, rows: MobLoadRow[]): Promise<MobLoadRow[]> {
  const res = await http.put<any>(`/bids/${uuid}/mob-loads`, {
    rows: rows.map((r, i) => ({
      label: r.label,
      heavy_loads: r.heavyLoads,
      legal_loads: r.legalLoads,
      pickup_loads: r.pickupLoads,
      sequence: i,
    })),
  });
  return (res?.data?.rows ?? []).map(mobRowFromApi);
}
